<template>
  <div :class="rootClasses">
    <HeadNav selectedLanguage="pt" :page="pageKey" />

    <main class="wg-main">
      <nav v-if="!isHomePage" class="wg-breadcrumb" aria-label="Breadcrumb">
        <template v-for="(crumb, index) in page.breadcrumb">
          <a :key="crumb.url" :href="crumb.url">{{ crumb.label }}</a>
          <span v-if="index < page.breadcrumb.length - 1" :key="`${crumb.url}-sep`">/</span>
        </template>
      </nav>

      <section :class="heroClasses">
        <div class="wg-hero-copy">
          <p class="wg-kicker">{{ kickerText }}</p>
          <h1>{{ page.title }}</h1>
          <p class="wg-subtitle" v-html="formatText(page.subtitle || page.metaDescription)"></p>
          <div v-if="page.badges" class="wg-badges">
            <span v-for="badge in page.badges" :key="badge">{{ badge }}</span>
          </div>
          <div v-if="page.cta && isDownloadPage" class="wg-actions wg-download-actions">
            <button class="wg-primary-btn" type="button" @click="downloadApk">
              <svg class="wg-download-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M11 3h2v9.17l3.59-3.58L18 10l-6 6-6-6 1.41-1.41L11 12.17V3zm-6 14h14v2H5v-2z"
                />
              </svg>
              {{ page.cta.label }}
            </button>
          </div>
          <div v-if="isSafePage" class="wg-safe-hero-audit" aria-label="Resumo de risco">
            <div>
              <span>Origem do APK</span>
              <strong>Risco alto</strong>
            </div>
            <div>
              <span>Permissões</span>
              <strong>Revisar sempre</strong>
            </div>
            <div>
              <span>Conta principal</span>
              <strong>Evite testar</strong>
            </div>
          </div>
          <div v-if="isUpdatePage" class="wg-update-hero-checks" aria-label="Resumo de atualização">
            <div>
              <span>Versão alvo</span>
              <strong> 18.80</strong>
            </div>
            <div>
              <span>Antes de tocar</span>
              <strong>Backup</strong>
            </div>
            <div>
              <span>Fluxo seguro</span>
              <strong>Instalar por cima</strong>
            </div>
          </div>
          <div v-if="isInstallPage" class="wg-install-hero-steps" aria-label="Resumo do guia de instalação">
            <span><strong>01</strong> Requisitos</span>
            <span><strong>02</strong> Fontes permitidas</span>
            <span><strong>03</strong> Restaurar backup</span>
          </div>
          <div v-if="isDownloadPage" class="wg-apk-summary">
            <div>
              <span>Versão</span>
              <strong> 18.80</strong>
            </div>
            <div>
              <span>Tamanho</span>
              <strong>80M</strong>
            </div>
            <div>
              <span>Android</span>
              <strong>5.0+</strong>
            </div>
          </div>
          <div v-if="page.cta && !isDownloadPage" :class="['wg-actions', { 'wg-actions-hub': isHubLayout }]">
            <a class="wg-primary-btn" :href="page.cta.url">{{ page.cta.label }}</a>
            <a v-if="showSecondaryCta" class="wg-secondary-btn" href="/gb-whatsapp-e-seguro/">Ler riscos</a>
          </div>
        </div>
        <div v-if="page.heroImage" class="wg-hero-media">
          <img :src="page.heroImage" :alt="page.title" />
        </div>
      </section>

      <section :class="contentClasses">
        <article :class="articleClasses">
          <div class="wg-intro">
            <p v-for="paragraph in page.intro" :key="paragraph" v-html="formatText(paragraph)"></p>
          </div>

          <div v-if="page.summaryCards" class="wg-summary-grid">
            <div v-for="card in page.summaryCards" :key="card.label" class="wg-summary-card">
              <span>{{ card.label }}</span>
              <strong>{{ card.value }}</strong>
              <p>{{ card.text }}</p>
            </div>
          </div>

          <section v-if="page.highlightCards" class="wg-home-panel">
            <div class="wg-home-panel-head">
              <p>{{ page.highlightLabel }}</p>
              <strong class="wg-home-panel-title">{{ page.highlightTitle }}</strong>
            </div>
            <div class="wg-highlight-grid">
              <article v-for="card in page.highlightCards" :key="card.title" class="wg-highlight-card">
                <span>{{ card.tag }}</span>
                <strong class="wg-highlight-title">{{ card.title }}</strong>
                <p>{{ card.text }}</p>
              </article>
            </div>
          </section>

          <div v-if="page.toc && !hideArticleToc" class="wg-toc">
            <p class="wg-toc-title">Conteúdo</p>
            <ol>
              <li v-for="item in tocEntries" :key="item.label">
                <a :href="item.href">{{ item.label }}</a>
              </li>
            </ol>
          </div>

          <div v-if="page.infoTable" id="download-info" class="wg-info-card">
            <h2>Informações do APK</h2>
            <table>
              <tbody>
                <tr v-for="row in page.infoTable" :key="row[0]">
                  <th>{{ row[0] }}</th>
                  <td>{{ row[1] }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="page.compareTable" class="wg-info-card">
            <h2>Tabela de comparação</h2>
            <table>
              <tbody>
                <tr v-for="(row, index) in page.compareTable" :key="row.join('-')" :class="{ 'wg-table-head': index === 0 }">
                  <th>{{ row[0] }}</th>
                  <td v-html="formatText(row[1])"></td>
                  <td v-html="formatText(row[2])"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <section
            v-for="(section, index) in page.sections"
            :id="sectionId(index)"
            :key="section.title"
            :class="sectionClasses(section, index)"
          >
            <div v-if="section.image && !isHomePage" class="wg-section-image">
              <img :src="section.image" :alt="section.title" />
            </div>
            <span v-if="isStepPage" class="wg-step-number">{{ String(index + 1).padStart(2, '0') }}</span>
            <span v-if="isRiskPage" class="wg-risk-label">Atenção</span>
            <h2 v-if="isSectionHeading(section)">{{ section.title }}</h2>
            <p v-else class="wg-section-title">{{ section.title }}</p>
            <p v-for="paragraph in section.paragraphs" :key="paragraph" v-html="formatText(paragraph)"></p>
            <ul v-if="section.list" class="wg-list">
              <li v-for="item in section.list" :key="item">{{ item }}</li>
            </ul>
            <div v-if="section.subsections" class="wg-subsections">
              <section v-for="subsection in section.subsections" :key="subsection.title" class="wg-subsection">
                <h3>{{ subsection.title }}</h3>
                <p v-for="paragraph in subsection.paragraphs" :key="paragraph" v-html="formatText(paragraph)"></p>
              </section>
            </div>
            <div v-if="section.downloadBox" class="wg-download-box">
              <div>
                <strong>WhatsApp GB  18.80</strong>
                <span>80M · Android 5.0+ · APK não hospedado aqui</span>
              </div>
              <button type="button" @click="downloadApk">
                <svg class="wg-download-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M11 3h2v9.17l3.59-3.58L18 10l-6 6-6-6 1.41-1.41L11 12.17V3zm-6 14h14v2H5v-2z"
                  />
                </svg>
                Baixar WhatsApp GB
              </button>
            </div>
            <a v-if="section.cta" class="wg-inline-link" :href="section.cta.url">{{ section.cta.label }}</a>
            <div v-if="showHomeCarouselAfter(index)" class="wg-home-carousel" aria-label="Capturas de tela do WhatsApp GB">
              <button class="wg-carousel-btn wg-carousel-prev" type="button" aria-label="Imagem anterior" @click="prevHomeSlide">&lsaquo;</button>
              <div ref="homeCarouselTrack" class="wg-carousel-track">
                <figure
                  v-for="(slide, slideIndex) in homeCarouselSlides"
                  :key="slide.image"
                  :class="['wg-carousel-slide', { 'is-active': slideIndex === activeHomeSlide }]"
                >
                  <img :src="slide.image" :alt="slide.title" />
                </figure>
              </div>
              <button class="wg-carousel-btn wg-carousel-next" type="button" aria-label="Próxima imagem" @click="nextHomeSlide">&rsaquo;</button>
              <div class="wg-carousel-dots" aria-label="Selecionar captura">
                <button
                  v-for="(slide, slideIndex) in homeCarouselSlides"
                  :key="`${slide.image}-dot`"
                  :class="{ 'is-active': slideIndex === activeHomeSlide }"
                  type="button"
                  :aria-label="`Mostrar ${slide.title}`"
                  @click="setHomeSlide(slideIndex)"
                ></button>
              </div>
            </div>
          </section>

          <section :id="faqId" :class="['wg-faq', `wg-faq-${layoutFamily}`]">
            <h2>Perguntas Frequentes</h2>
            <div v-for="item in page.faq" :key="item.q" class="wg-faq-item">
              <h3 v-if="!isHomePage">{{ item.q }}</h3>
              <strong v-else class="wg-faq-question">{{ item.q }}</strong>
              <p v-html="formatText(item.a)"></p>
            </div>
          </section>

          <section
            v-if="page.conclusion"
            :id="conclusionId"
            :class="['wg-conclusion', `wg-conclusion-${layoutFamily}`]"
          >
            <h2>Conclusão</h2>
            <p v-html="formatText(page.conclusion)"></p>
          </section>

          <aside :class="['wg-disclaimer', `wg-disclaimer-${layoutFamily}`]">
            {{ disclaimerText }}
          </aside>
        </article>

        <aside v-if="showSidebar" :class="sidebarClasses">
          <div v-if="sidebarVariant === 'toc'" class="wg-sticky-card wg-sidebar-toc">
            <strong class="wg-sticky-title">Neste guia</strong>
            <ol>
              <li v-for="item in tocEntries" :key="item.label">
                <a :href="item.href">{{ item.label }}</a>
              </li>
            </ol>
          </div>

          <div v-else-if="sidebarVariant === 'decision'" class="wg-sticky-card wg-sidebar-decision">
            <strong class="wg-sticky-title">Qual escolher?</strong>
            <div class="wg-decision-item wg-decision-gb">
              <span>WhatsApp GB</span>
              <p>Personalização, recursos extras e mais trabalho manual.</p>
            </div>
            <div class="wg-decision-item wg-decision-official">
              <span>WhatsApp oficial</span>
              <p>Loja oficial, suporte e mais estabilidade para conta principal.</p>
            </div>
            <nav class="wg-sidebar-links" aria-label="Guias relacionados">
              <a v-for="link in sidebarLinks" :key="link.url" :href="link.url">{{ link.label }}</a>
            </nav>
          </div>

          <div v-else-if="sidebarVariant === 'triage'" class="wg-sticky-card wg-sidebar-triage">
            <strong class="wg-sticky-title">{{ sidebarTitle }}</strong>
            <p class="wg-sidebar-lead">Leia nesta ordem se precisar de mais contexto.</p>
            <nav class="wg-sidebar-links" aria-label="Guias relacionados">
              <a v-for="link in sidebarLinks" :key="link.url" :href="link.url">{{ link.label }}</a>
            </nav>
          </div>
        </aside>
      </section>
    </main>
  </div>
</template>

<script>
import HeadNav from '@/components/HeadNav.vue';
import { pages, disclaimerText } from '@/documents/sitePages.js';

// 侧栏相关指南映射（仅 UI，不改正文）
const SIDEBAR_LINK_KEYS = {
  notWorking: ['update', 'install', 'banned'],
  banned: ['safe', 'notWorking', 'compare'],
  safe: ['compare', 'banned', 'install'],
  compare: ['safe', 'install', 'download'],
  download: ['install', 'safe', 'update']
};

const PAGE_LINK_META = {
  download: { label: 'Download', url: '/download/' },
  update: { label: 'Atualizar', url: '/como-atualizar-gb-whatsapp/' },
  install: { label: 'Instalar', url: '/como-instalar-gb-whatsapp/' },
  notWorking: { label: 'Não funciona', url: '/gb-whatsapp-nao-funciona/' },
  banned: { label: 'Bloqueado', url: '/gb-whatsapp-foi-bloqueado/' },
  safe: { label: 'É seguro?', url: '/gb-whatsapp-e-seguro/' },
  compare: { label: 'Comparativo', url: '/gb-whatsapp-vs-whatsapp/' }
};

const LAYOUT_FAMILY_BY_PAGE = {
  home: 'hub',
  download: 'product',
  update: 'guide',
  install: 'guide',
  notWorking: 'diagnostic',
  banned: 'diagnostic',
  safe: 'diagnostic',
  compare: 'table',
  privacy: 'doc',
  about: 'doc',
  disclaimer: 'doc'
};

export default {
  name: 'SitePage',
  components: {
    HeadNav
  },
  props: {
    pageKey: {
      type: String,
      default: 'home'
    }
  },
  data() {
    return {
      disclaimerText,
      apk: null,
      apkLoading: false,
      activeHomeSlide: 0,
      homeCarouselTimer: null
    };
  },
  computed: {
    page() {
      return pages[this.pageKey] || pages.home;
    },
    rootClasses() {
      return [
        'wg-page',
        `wg-page-${this.pageKey}`,
        `wg-type-${this.page.type}`,
        `wg-layout-${this.layoutFamily}`
      ];
    },
    layoutFamily() {
      return LAYOUT_FAMILY_BY_PAGE[this.pageKey] || 'hub';
    },
    isHubLayout() {
      return this.layoutFamily === 'hub';
    },
    heroClasses() {
      const forceNoMedia = ['product', 'doc'].includes(this.layoutFamily);

      return [
        'wg-hero',
        `wg-hero-${this.pageKey}`,
        `wg-hero-${this.layoutFamily}`,
        {
          'wg-hero-no-media': forceNoMedia || !this.page.heroImage,
          'wg-hero-compact': this.layoutFamily === 'diagnostic',
          'wg-hero-table': this.layoutFamily === 'table'
        }
      ];
    },
    contentClasses() {
      return [
        'wg-content',
        `wg-content-${this.pageKey}`,
        `wg-content-${this.layoutFamily}`,
        { 'wg-content-full': !this.showSidebar }
      ];
    },
    articleClasses() {
      return ['wg-article', `wg-article-${this.pageKey}`, `wg-article-${this.layoutFamily}`];
    },
    sidebarClasses() {
      return ['wg-sidebar', `wg-sidebar-${this.layoutFamily}`, `wg-sidebar-${this.sidebarVariant}`];
    },
    showSidebar() {
      if (this.sidebarVariant === 'toc') {
        return this.tocEntries.length > 0;
      }
      if (this.sidebarVariant === 'decision') {
        return true;
      }
      if (this.sidebarVariant === 'triage') {
        return this.sidebarLinks.length > 0;
      }
      return false;
    },
    sidebarVariant() {
      if (this.layoutFamily === 'guide') {
        return 'toc';
      }
      if (this.layoutFamily === 'table') {
        return 'decision';
      }
      if (this.layoutFamily === 'diagnostic' || this.layoutFamily === 'product') {
        return 'triage';
      }
      return 'none';
    },
    sidebarTitle() {
      if (this.layoutFamily === 'diagnostic') {
        return 'O que fazer agora';
      }
      if (this.layoutFamily === 'product') {
        return 'Próximos passos';
      }
      return 'Guias relacionados';
    },
    sidebarLinks() {
      const keys = SIDEBAR_LINK_KEYS[this.pageKey] || [];
      return keys.map((key) => PAGE_LINK_META[key]).filter(Boolean);
    },
    hideArticleToc() {
      return this.layoutFamily === 'guide';
    },
    showSecondaryCta() {
      return ['hub', 'guide'].includes(this.layoutFamily) && this.page.cta;
    },
    isDownloadPage() {
      return this.pageKey === 'download';
    },
    isUpdatePage() {
      return this.pageKey === 'update';
    },
    isInstallPage() {
      return this.pageKey === 'install';
    },
    isSafePage() {
      return this.pageKey === 'safe';
    },
    isHomePage() {
      return this.pageKey === 'home';
    },
    homeCarouselSlides() {
      if (!this.isHomePage) {
        return [];
      }

      return (this.page.sections || [])
        .filter((section) => section.image)
        .map((section) => ({
          title: section.title,
          image: section.image
        }));
    },
    isStepPage() {
      return ['update', 'install'].includes(this.pageKey);
    },
    isRiskPage() {
      return ['notWorking', 'banned', 'safe'].includes(this.pageKey);
    },
    kickerText() {
      const labels = {
        home: 'Guia sobre WhatsApp GB',
        download: 'Informações do APK',
        update: 'Atualização segura',
        install: 'Instalação no Android',
        notWorking: 'Diagnóstico e correções',
        banned: 'Bloqueio de conta',
        safe: 'Privacidade e segurança',
        compare: 'Comparativo direto',
        privacy: 'Documento do site',
        about: 'Sobre o projeto',
        disclaimer: 'Aviso importante'
      };
      return labels[this.pageKey] || 'Guia sobre WhatsApp GB';
    },
    faqId() {
      return `faq-${this.pageKey}`;
    },
    conclusionId() {
      return `conclusion-${this.pageKey}`;
    },
    tocEntries() {
      if (!this.page.toc) {
        return [];
      }

      return this.page.toc.map((label) => {
        const isFaqItem = /perguntas frequentes/i.test(label);
        const isConclusionItem = /conclus/i.test(label);
        return {
          label,
          href: `#${
            isFaqItem
              ? this.faqId
              : isConclusionItem
                ? this.conclusionId
                : this.sectionIdByTitle(label)
          }`
        };
      });
    }
  },
  watch: {
    pageKey() {
      this.activeHomeSlide = 0;
      this.restartHomeCarousel();
    }
  },
  mounted() {
    this.apk = this.findApkUrl(this.$root && this.$root.initData) || this.findApkUrl(typeof initData !== 'undefined' ? initData : null);
    this.restartHomeCarousel();
  },
  beforeDestroy() {
    this.stopHomeCarousel();
  },
  methods: {
    normalizeHeading(value) {
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    },
    sectionIdByTitle(title) {
      const sections = this.page.sections || [];
      const index = sections.findIndex(
        (section) =>
          this.isSectionHeading(section) &&
          this.normalizeHeading(section.title) === this.normalizeHeading(title)
      );

      return index >= 0 ? this.sectionId(index) : this.faqId;
    },
    sectionId(index) {
      return `section-${this.pageKey}-${index + 1}`;
    },
    isSectionHeading(section) {
      return section.heading !== false;
    },
    showHomeCarouselAfter(index) {
      if (!this.isHomePage || this.homeCarouselSlides.length === 0) {
        return false;
      }

      const targetIndex = (this.page.sections || []).findIndex((section) =>
        this.normalizeHeading(section.title).includes('principais recursos')
      );

      return index === targetIndex;
    },
    setHomeSlide(index) {
      if (!this.homeCarouselSlides.length) {
        return;
      }

      this.activeHomeSlide = index;
      this.syncHomeCarouselScroll();
      this.restartHomeCarousel();
    },
    nextHomeSlide() {
      if (!this.homeCarouselSlides.length) {
        return;
      }

      this.activeHomeSlide = (this.activeHomeSlide + 1) % this.homeCarouselSlides.length;
      this.syncHomeCarouselScroll();
      this.restartHomeCarousel();
    },
    prevHomeSlide() {
      if (!this.homeCarouselSlides.length) {
        return;
      }

      this.activeHomeSlide =
        (this.activeHomeSlide - 1 + this.homeCarouselSlides.length) % this.homeCarouselSlides.length;
      this.syncHomeCarouselScroll();
      this.restartHomeCarousel();
    },
    restartHomeCarousel() {
      this.stopHomeCarousel();

      if (!this.isHomePage || this.homeCarouselSlides.length < 2 || typeof window === 'undefined') {
        return;
      }

      this.homeCarouselTimer = window.setInterval(() => {
        this.activeHomeSlide = (this.activeHomeSlide + 1) % this.homeCarouselSlides.length;
        this.syncHomeCarouselScroll();
      }, 4500);
    },
    stopHomeCarousel() {
      if (this.homeCarouselTimer && typeof window !== 'undefined') {
        window.clearInterval(this.homeCarouselTimer);
      }

      this.homeCarouselTimer = null;
    },
    syncHomeCarouselScroll() {
      if (typeof window === 'undefined') {
        return;
      }

      this.$nextTick(() => {
        const track = Array.isArray(this.$refs.homeCarouselTrack)
          ? this.$refs.homeCarouselTrack[0]
          : this.$refs.homeCarouselTrack;
        const activeSlide = track && track.children[this.activeHomeSlide];

        if (!track || !activeSlide) {
          return;
        }

        track.scrollTo({
          left: activeSlide.offsetLeft - track.offsetLeft,
          behavior: 'smooth'
        });
      });
    },
    findApkUrl(value) {
      if (!value) {
        return null;
      }
      if (typeof value === 'string') {
        return /(\.apk|gbwhatsapp\.download|^https?:\/\/)/i.test(value) ? value : null;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          const result = this.findApkUrl(item);
          if (result) {
            return result;
          }
        }
        return null;
      }
      if (typeof value === 'object') {
        if (typeof value.apk === 'string') {
          return value.apk;
        }
        for (const key of Object.keys(value)) {
          const result = this.findApkUrl(value[key]);
          if (result) {
            return result;
          }
        }
      }
      return null;
    },
    async ensureApk() {
      if (this.apk) {
        return this.apk;
      }
      if (this.page.downloadUrl) {
        this.apk = this.page.downloadUrl;
        return this.apk;
      }
      if (this.apkLoading) {
        return null;
      }

      this.apkLoading = true;
      try {
        const response = await this.$server.getOfficalApk({
          domain: 'gbwhatsapp.chat',
          appName: 'GB_gbwhatsapp.chat'
        });

        if (response && response.code === 200 && response.data && response.data.apk) {
          this.apk = response.data.apk;
        } else {
          this.apk = process.env.VUE_APP_GBLINK;
        }
      } catch (error) {
        this.apk = process.env.VUE_APP_GBLINK;
      } finally {
        this.apkLoading = false;
      }

      return this.apk;
    },
    async downloadApk() {
      const apk = await this.ensureApk();
      if (apk) {
        this.$global.download(apk);
      }
    },
    formatText(text) {
      if (!text) {
        return '';
      }

      return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
    },
    sectionClasses(section, index) {
      return [
        'wg-section',
        `wg-section-${this.pageKey}`,
        `wg-section-${this.layoutFamily}`,
        {
          'wg-section-with-image': section.image && !this.isHomePage,
          'wg-section-download': section.downloadBox,
          'wg-section-even': index % 2 === 1
        }
      ];
    }
  }
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Oswald:wght@400;500;600&display=swap');

.wg-page {
  --wg-accent: #16a34a;
  --wg-accent-dark: #145c39;
  --wg-soft: #e8f7ed;
  --wg-panel: #ffffff;
  --wg-font-body: 'Lora', Georgia, 'Times New Roman', serif;
  --wg-font-heading: 'Oswald', 'Arial Narrow', Arial, sans-serif;
  min-height: 100vh;
  max-width: 100%;
  overflow-x: hidden;
  overflow-x: clip;
  background: #f6f8f7;
  color: #12201a;
  font-family: var(--wg-font-body);
  font-size: 18px;
  line-height: 1.625;
  -webkit-font-smoothing: antialiased;
}

.wg-page,
.wg-page * {
  box-sizing: border-box;
}

.wg-page-download {
  --wg-accent: #11a765;
  --wg-accent-dark: #0d4f37;
  --wg-soft: #e7fff2;
  background: #f2f7f4;
}

.wg-page-update {
  --wg-accent: #2563eb;
  --wg-accent-dark: #1e3a8a;
  --wg-soft: #eaf1ff;
  background: #f4f7ff;
}

.wg-page-install {
  --wg-accent: #0d9488;
  --wg-accent-dark: #0f3f3c;
  --wg-soft: #e8f7f4;
  background: linear-gradient(180deg, #f1faf6 0%, #f8fafc 42%, #f6f8f7 100%);
}

.wg-page-notWorking {
  --wg-accent: #f59e0b;
  --wg-accent-dark: #7c4a03;
  --wg-soft: #fff7df;
  background: #fffaf0;
}

.wg-page-banned {
  --wg-accent: #dc2626;
  --wg-accent-dark: #7f1d1d;
  --wg-soft: #fff1f1;
  background: #fff7f7;
}

.wg-page-safe {
  --wg-accent: #d97706;
  --wg-accent-dark: #7c2d12;
  --wg-soft: #fff7ed;
  background: linear-gradient(180deg, #fffaf3 0%, #f8fafc 46%, #f6f8f7 100%);
}

.wg-page-compare {
  --wg-accent: #4f46e5;
  --wg-accent-dark: #312e81;
  --wg-soft: #eef0ff;
  background: #f6f7ff;
}

.wg-type-legal {
  --wg-accent: #334155;
  --wg-accent-dark: #0f172a;
  --wg-soft: #f1f5f9;
  background: #f8fafc;
}

.wg-main {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 22px 0 64px;
}

.wg-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #647067;
  font-size: 14px;
  margin: 8px 0 18px;

  a {
    color: #167a46;
    text-decoration: none;
  }
}

.wg-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 520px);
  gap: 34px;
  align-items: center;
  padding: 42px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eafaf0 0%, #ffffff 54%, #eaf2ff 100%);
  box-shadow: 0 18px 50px rgba(15, 31, 25, 0.08);
  overflow: hidden;
}

.wg-hero-home {
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 560px);
  min-height: 440px;
  gap: 26px;
  padding: 38px 42px;
  background: radial-gradient(circle at 86% 18%, rgba(187, 247, 208, 0.36), transparent 25%),
    linear-gradient(135deg, #071b13 0%, #0f5136 46%, #12865c 100%);
  color: #ffffff;
  box-shadow: 0 26px 70px rgba(5, 52, 34, 0.24);
}

.wg-hero-home h1 {
  color: #ffffff !important;
  font-size: 42px;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.26);
}

.wg-hero-home .wg-kicker,
.wg-hero-home .wg-subtitle {
  color: #ffffff;
}

.wg-hero-home .wg-subtitle {
  max-width: 720px;
  font-size: 18px;
  margin-top: 12px;
  line-height: 1.52;
  color: rgba(255, 255, 255, 0.86);
}

.wg-hero-home .wg-badges {
  gap: 8px;
  margin-top: 16px;
}

.wg-hero-home .wg-badges span {
  padding: 7px 11px;
  border-color: rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.wg-hero-home .wg-actions {
  margin-top: 20px;
}

.wg-hero-home .wg-primary-btn {
  background: #22c55e;
  box-shadow: 0 14px 30px rgba(34, 197, 94, 0.28);
}

.wg-hero-home .wg-secondary-btn {
  border-color: rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.wg-hero-home .wg-hero-media {
  padding: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  overflow: hidden;
}

.wg-hero-home .wg-hero-media img {
  max-height: 340px;
  object-fit: cover;
}

.wg-hero-download {
  grid-template-columns: minmax(0, 1fr) minmax(280px, 380px);
  padding: 34px 38px;
  background: linear-gradient(135deg, #ffffff 0%, #f1fbf5 100%);
  border: 1px solid #cdebd9;
  border-top: 6px solid var(--wg-accent);
  color: #12201a;
  box-shadow: 0 16px 38px rgba(11, 103, 69, 0.12);
}

.wg-hero-no-media {
  grid-template-columns: minmax(0, 1fr);
}

.wg-hero-no-media .wg-hero-copy {
  max-width: 760px;
}

.wg-hero-notWorking.wg-hero-no-media,
.wg-hero-banned.wg-hero-no-media,
.wg-hero-compare.wg-hero-no-media {
  align-items: start;
  padding: 36px 40px;
}

.wg-hero-notWorking.wg-hero-no-media .wg-hero-copy,
.wg-hero-banned.wg-hero-no-media .wg-hero-copy,
.wg-hero-compare.wg-hero-no-media .wg-hero-copy {
  max-width: 860px;
}

.wg-hero-notWorking.wg-hero-no-media h1,
.wg-hero-banned.wg-hero-no-media h1,
.wg-hero-compare.wg-hero-no-media h1 {
  max-width: 820px;
}

.wg-hero-notWorking.wg-hero-no-media .wg-subtitle,
.wg-hero-banned.wg-hero-no-media .wg-subtitle,
.wg-hero-compare.wg-hero-no-media .wg-subtitle {
  max-width: 760px;
}

.wg-hero-download.wg-hero-no-media {
  padding-right: 46px;
}

.wg-hero-download.wg-hero-no-media .wg-hero-copy,
.wg-hero-download.wg-hero-no-media h1,
.wg-hero-download.wg-hero-no-media .wg-subtitle {
  max-width: none;
}

.wg-hero-download.wg-hero-no-media .wg-apk-summary {
  max-width: none;
}

.wg-hero-download h1,
.wg-hero-download .wg-kicker,
.wg-hero-download .wg-subtitle {
  color: #12201a;
}

.wg-hero-download h1 {
  max-width: 640px;
  font-size: 36px;
}

.wg-hero-download .wg-subtitle {
  max-width: 650px;
  color: #405147;
}

.wg-hero-download .wg-kicker {
  color: var(--wg-accent-dark);
}

.wg-hero-download .wg-badges span,
.wg-hero-download .wg-secondary-btn {
  border-color: #cdebd9;
  background: #ffffff;
  color: var(--wg-accent-dark);
}

.wg-hero-download .wg-primary-btn {
  box-shadow: 0 10px 20px rgba(17, 167, 101, 0.18);
}

.wg-hero-download .wg-download-actions {
  margin-top: 16px;
  margin-bottom: 26px;
}

.wg-hero-download .wg-download-actions .wg-primary-btn {
  min-width: 360px;
  min-height: 64px;
  padding: 0 34px;
  gap: 10px;
  border-radius: 999px;
  font-size: 20px;
  box-shadow: 0 18px 34px rgba(17, 167, 101, 0.24);
}

.wg-download-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.wg-hero-download .wg-download-actions .wg-download-icon {
  width: 24px;
  height: 24px;
}

.wg-hero-download .wg-hero-media {
  align-self: center;
}

.wg-hero-download .wg-hero-media img {
  max-height: 240px;
  object-fit: contain;
}

.wg-hero-update,
.wg-hero-install {
  align-items: stretch;
  background: linear-gradient(135deg, #ffffff 0%, var(--wg-soft) 100%);
  border: 1px solid rgba(37, 99, 235, 0.12);
}

.wg-hero-update {
  grid-template-columns: minmax(0, 1fr) minmax(300px, 420px);
  gap: 28px;
  padding: 38px;
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #eef5ff 58%, #f8fbff 100%);
  border: 1px solid #c9daf8;
  border-top: 8px solid #2563eb;
  box-shadow: 0 18px 44px rgba(30, 58, 138, 0.12);
}

.wg-hero-update h1 {
  max-width: 680px;
  color: #10203f;
}

.wg-hero-update .wg-kicker {
  color: #1e3a8a;
}

.wg-hero-update .wg-subtitle {
  max-width: 680px;
  color: #334155;
}

.wg-update-hero-checks {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 24px;
}

.wg-update-hero-checks div {
  min-height: 82px;
  padding: 14px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #d7e3f7;
  box-shadow: 0 10px 24px rgba(30, 58, 138, 0.06);
}

.wg-update-hero-checks span {
  display: block;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
}

.wg-update-hero-checks strong {
  display: block;
  color: #1e3a8a;
  font-family: var(--wg-font-heading);
  font-size: 22px;
  font-weight: 400;
  line-height: 1.15;
}

.wg-hero-update .wg-hero-media {
  justify-self: end;
  width: min(100%, 390px);
  padding: 18px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #d7e3f7;
  box-shadow: 0 18px 38px rgba(30, 58, 138, 0.12);
}

.wg-hero-update .wg-hero-media img {
  max-height: 300px;
  object-fit: contain;
}

.wg-hero-install {
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  padding: 40px 44px 34px;
  align-items: start;
  background: linear-gradient(135deg, #ffffff 0%, #f3fbf7 58%, #e8f7f0 100%);
  border: 1px solid #cfe9df;
  border-left: 8px solid #18a16f;
  color: #12201a;
  box-shadow: 0 18px 44px rgba(15, 63, 60, 0.12);
}

.wg-hero-install .wg-hero-copy {
  max-width: 900px;
}

.wg-hero-install h1,
.wg-hero-install .wg-kicker,
.wg-hero-install .wg-subtitle {
  color: #12201a;
}

.wg-hero-install h1 {
  max-width: 820px;
  font-size: 42px;
}

.wg-hero-install .wg-subtitle {
  max-width: 820px;
  color: #405147;
}

.wg-install-hero-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: min(100%, 820px);
  margin-top: 24px;
}

.wg-install-hero-steps span {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #cfe9df;
  color: #145c39;
  font-weight: 700;
  box-shadow: 0 8px 20px rgba(15, 63, 60, 0.06);
}

.wg-install-hero-steps strong {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #e8f7f0;
  color: #0f4f48;
  font-size: 13px;
}

.wg-hero-install .wg-hero-media {
  justify-self: stretch;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #cfe9df;
  box-shadow: 0 16px 34px rgba(15, 63, 60, 0.1);
}

.wg-hero-install .wg-hero-media img {
  max-height: 250px;
  object-fit: contain;
}

.wg-hero-notWorking,
.wg-hero-banned,
.wg-hero-safe {
  background: linear-gradient(135deg, #ffffff 0%, var(--wg-soft) 66%, #ffffff 100%);
  border-left: 8px solid var(--wg-accent);
}

.wg-hero-safe {
  grid-template-columns: minmax(0, 1fr) minmax(300px, 430px);
  gap: 30px;
  padding: 38px;
  background: linear-gradient(135deg, #ffffff 0%, #fff7ed 52%, #eff6ff 100%);
  border: 1px solid #fed7aa;
  border-left: 0;
  border-top: 8px solid #d97706;
  box-shadow: 0 18px 46px rgba(124, 45, 18, 0.11);
}

.wg-hero-safe h1 {
  max-width: 680px;
}

.wg-hero.wg-hero-safe h1 {
  color: #1f2937;
}

.wg-hero.wg-hero-safe .wg-kicker {
  color: #7c2d12;
}

.wg-hero.wg-hero-safe .wg-subtitle {
  max-width: 700px;
  color: #4b5563;
}

.wg-safe-hero-audit {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 24px;
}

.wg-safe-hero-audit div {
  min-height: 84px;
  padding: 14px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #fed7aa;
  box-shadow: 0 10px 24px rgba(124, 45, 18, 0.06);
}

.wg-safe-hero-audit span {
  display: block;
  margin-bottom: 8px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.wg-safe-hero-audit strong {
  display: block;
  color: #7c2d12;
  font-family: var(--wg-font-heading);
  font-size: 22px;
  font-weight: 400;
  line-height: 1.15;
}

.wg-hero-safe .wg-hero-media {
  justify-self: end;
  width: min(100%, 410px);
  padding: 18px;
  border-radius: 8px;
  background: #111827;
  border: 1px solid #1f2937;
  box-shadow: 0 20px 42px rgba(17, 24, 39, 0.18);
}

.wg-hero-safe .wg-hero-media img {
  max-height: 300px;
  object-fit: contain;
}

.wg-hero-compare {
  background: linear-gradient(135deg, #eef0ff 0%, #ffffff 52%, #ecfdf5 100%);
}

.wg-type-legal .wg-hero {
  grid-template-columns: 1fr;
  padding: 34px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: none;
}

.wg-kicker {
  margin: 0 0 12px;
  color: var(--wg-accent-dark);
  font-family: var(--wg-font-heading);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 13px;
}

.wg-hero h1 {
  margin: 0;
  font-family: var(--wg-font-heading);
  font-size: 36px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0;
  color: #0c1f16;
}

.wg-hero.wg-hero-download h1,
.wg-hero.wg-hero-download .wg-subtitle,
.wg-hero.wg-hero-download .wg-kicker {
  color: inherit;
}

.wg-hero.wg-hero-download .wg-subtitle {
  color: #405147;
}

.wg-hero.wg-hero-download .wg-kicker {
  color: var(--wg-accent-dark);
}

.wg-hero.wg-hero-update h1 {
  color: #10203f;
}

.wg-hero.wg-hero-update .wg-kicker {
  color: #1e3a8a;
}

.wg-hero.wg-hero-update .wg-subtitle {
  color: #334155;
}

.wg-hero.wg-hero-install h1,
.wg-hero.wg-hero-install .wg-kicker,
.wg-hero.wg-hero-install .wg-subtitle {
  color: #12201a;
}

.wg-hero.wg-hero-install .wg-subtitle {
  color: #405147;
}

.wg-subtitle {
  margin: 18px 0 0;
  max-width: 680px;
  font-size: 18px;
  line-height: 1.625;
  color: #405147;
}

.wg-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;

  span {
    padding: 9px 13px;
    border: 1px solid #cfe7d8;
    border-radius: 6px;
    background: #ffffff;
    color: #145c39;
    font-weight: 700;
    font-size: 14px;
  }
}

.wg-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.wg-primary-btn,
.wg-secondary-btn,
.wg-inline-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 6px;
  text-decoration: none;
  font-family: var(--wg-font-heading);
  font-weight: 700;
  letter-spacing: 0;
}

.wg-primary-btn {
  border: 0;
  color: #fff;
  background: var(--wg-accent);
  box-shadow: 0 10px 22px rgba(22, 163, 74, 0.18);
  cursor: pointer;
}

.wg-secondary-btn {
  color: var(--wg-accent-dark);
  background: #ffffff;
  border: 1px solid #cfe7d8;
}

.wg-apk-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 24px;
  max-width: 520px;

  div {
    padding: 14px;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid #cdebd9;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #607067;
    font-size: 13px;
  }

  strong {
    margin-top: 4px;
    font-size: 24px;
    color: var(--wg-accent-dark);
  }
}

.wg-hero-media img,
.wg-section-image img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

.wg-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 28px;
  margin-top: 28px;
  align-items: start;
}

.wg-article,
.wg-sticky-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(15, 31, 25, 0.07);
}

.wg-article {
  min-width: 0;
  padding: 34px;
}

.wg-article-download {
  border-top: 6px solid var(--wg-accent);
}

.wg-article-update,
.wg-article-install {
  background: transparent;
  box-shadow: none;
  padding: 0;
}

/* --- 布局族：整体栅格与侧栏 --- */
.wg-layout-hub .wg-content,
.wg-layout-doc .wg-content,
.wg-layout-product .wg-content {
  grid-template-columns: minmax(0, 1fr);
}

.wg-layout-guide .wg-content,
.wg-layout-diagnostic .wg-content,
.wg-layout-table .wg-content {
  grid-template-columns: minmax(0, 1fr) 280px;
}

.wg-content.wg-content-full {
  grid-template-columns: minmax(0, 1fr);
}

.wg-layout-hub .wg-sidebar,
.wg-layout-doc .wg-sidebar {
  display: none;
}

.wg-layout-guide .wg-article > .wg-toc {
  display: none;
}

.wg-layout-doc .wg-article {
  max-width: 1180px;
  width: 100%;
  margin: 0 auto;
  box-shadow: none;
  border: 1px solid #e2e8f0;
  background: #ffffff;
}

.wg-layout-product .wg-article {
  border-top: 6px solid var(--wg-accent);
}

.wg-layout-product .wg-content {
  gap: 20px;
}

.wg-layout-guide .wg-main > .wg-hero {
  margin-bottom: 0;
}

.wg-layout-guide .wg-content {
  margin-top: 18px;
}

/* --- Hero 布局族 --- */
.wg-hero-product,
.wg-hero-doc {
  grid-template-columns: minmax(0, 1fr);
  padding: 30px 36px;
  min-height: auto;
  box-shadow: 0 12px 30px rgba(15, 31, 25, 0.06);
}

.wg-hero-product {
  border-top: 8px solid var(--wg-accent);
}

.wg-hero-doc {
  border: 1px solid #e2e8f0;
  box-shadow: none;
  background: #ffffff;
}

.wg-hero-compact {
  min-height: auto;
  padding: 28px 34px;
  border-top: 8px solid var(--wg-accent);
}

.wg-hero-compact h1 {
  font-size: 32px;
}

.wg-hero-compact .wg-subtitle {
  margin-top: 12px;
  font-size: 17px;
}

.wg-hero-compact .wg-hero-media img {
  max-height: 220px;
}

.wg-hero-table {
  margin-bottom: 0;
  padding: 26px 32px;
  min-height: auto;
}

.wg-actions-hub .wg-secondary-btn {
  opacity: 0.72;
  background: rgba(255, 255, 255, 0.08);
}

/* --- 侧栏变体 --- */
.wg-sidebar-toc ol {
  margin: 0;
  padding-left: 18px;
  color: #334139;
  line-height: 1.75;
}

.wg-sidebar-toc li + li {
  margin-top: 6px;
}

.wg-sidebar-toc a {
  color: #183329;
  text-decoration: none;
  font-size: 15px;
}

.wg-sidebar-toc a:hover {
  color: var(--wg-accent-dark);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.wg-sidebar-lead {
  margin: 0 0 14px;
  color: #647067;
  font-size: 14px;
  line-height: 1.55;
}

.wg-sidebar-links {
  display: grid;
  gap: 8px;
}

.wg-sidebar-links a {
  display: block;
  padding: 11px 12px;
  border-radius: 6px;
  background: var(--wg-soft);
  border: 1px solid rgba(0, 0, 0, 0.06);
  color: var(--wg-accent-dark);
  text-decoration: none;
  font-family: var(--wg-font-heading);
  font-size: 15px;
  font-weight: 400;
}

.wg-sidebar-links a:hover {
  background: #ffffff;
  border-color: var(--wg-accent);
}

.wg-sidebar-decision .wg-decision-item {
  display: block;
  padding: 12px 14px;
  border-radius: 6px;
  margin-bottom: 10px;
  border-bottom: 0;
  text-align: left;
}

.wg-sidebar-decision .wg-decision-item span {
  display: block;
  margin-bottom: 6px;
  font-family: var(--wg-font-heading);
  color: #10251a;
  font-size: 16px;
  line-height: 1.25;
}

.wg-sidebar-decision .wg-decision-item p {
  margin: 0;
  color: #405147;
  font-size: 14px;
  line-height: 1.5;
}

.wg-decision-gb {
  background: #eef0ff;
  border: 1px solid #c7d2fe;
}

.wg-decision-official {
  background: #e8f7ed;
  border: 1px solid #cfe7d8;
}

.wg-sidebar-decision .wg-sidebar-links {
  margin-top: 14px;
}

/* --- 正文 section 布局族 --- */
.wg-section-diagnostic {
  padding: 22px 22px 22px 26px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eadfd0;
  border-left: 6px solid var(--wg-accent);
  box-shadow: none;
}

.wg-section-diagnostic.wg-section-even {
  background: var(--wg-soft);
}

.wg-section-table {
  padding: 24px 0;
  margin-top: 28px;
  border-top: 1px solid #d8ddff;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.wg-section-table:first-of-type {
  border-top: 0;
  margin-top: 24px;
}

.wg-section-doc {
  margin-top: 0;
  padding: 24px 0 0;
  border-top: 1px solid #e2e8f0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.wg-section-doc:first-of-type {
  padding-top: 0;
  border-top: 0;
}

.wg-section-hub {
  margin-top: 38px;
}

.wg-layout-table .wg-info-card {
  margin-top: 12px;
  margin-bottom: 8px;
  box-shadow: 0 16px 36px rgba(79, 70, 229, 0.1);
}

.wg-layout-product .wg-info-card {
  margin-top: 8px;
}

/* --- FAQ / 结论 / 免责声明 布局族 --- */
.wg-faq-hub .wg-faq-item {
  padding: 18px;
  border: 1px solid #dbeee2;
  border-radius: 8px;
  background: #f7faf8;
  border-bottom: 1px solid #dbeee2;
}

.wg-faq-hub {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.wg-faq-hub > h2 {
  grid-column: 1 / -1;
}

.wg-faq-diagnostic .wg-faq-item {
  padding: 16px 16px 16px 20px;
  border-left: 5px solid var(--wg-accent);
  border-bottom: 1px solid #eadfd0;
  background: #ffffff;
}

.wg-faq-doc .wg-faq-item {
  padding: 14px 0;
  border-bottom: 1px solid #e2e8f0;
}

.wg-faq-doc .wg-faq-item h3 {
  font-size: 20px;
}

.wg-conclusion-doc {
  margin-top: 28px;
  padding: 20px 0 0;
  border: 0;
  border-top: 1px solid #e2e8f0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.wg-conclusion-diagnostic {
  border-left: 6px solid var(--wg-accent);
}

.wg-conclusion-table {
  background: linear-gradient(135deg, #eef0ff 0%, #ffffff 100%);
  border-color: #c7d2fe;
}

.wg-disclaimer-doc {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 500;
}

.wg-intro p,
.wg-section p,
.wg-faq p,
.wg-related p,
.wg-conclusion p {
  font-size: 18px;
  line-height: 1.625;
  color: #334139;
}

.wg-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin: 28px 0;
}

.wg-summary-card {
  padding: 18px;
  border-radius: 8px;
  background: #f7faf8;
  border: 1px solid #dbeee2;

  span,
  strong,
  p {
    display: block;
  }

  span {
    color: #607067;
    font-size: 13px;
    font-weight: 700;
  }

  strong {
    margin-top: 8px;
    font-family: var(--wg-font-heading);
    color: #10251a;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.2;
  }

  p {
    margin: 10px 0 0;
    color: #405147;
    font-size: 14px;
    line-height: 1.55;
  }
}

.wg-home-panel {
  margin: 30px 0;
  padding: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eefaf3 0%, #ffffff 100%);
  border: 1px solid #cfe7d8;
}

.wg-home-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 18px;
  margin-bottom: 20px;

  p {
    margin: 0;
    color: #167a46;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .wg-home-panel-title {
    display: block;
    margin: 6px 0 0;
    font-family: var(--wg-font-heading);
    color: #10251a;
    font-size: 30px;
    font-weight: 400;
    line-height: 1.2;
  }
}

.wg-highlight-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.wg-highlight-card {
  padding: 18px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #dcebe2;

  span {
    display: inline-flex;
    margin-bottom: 10px;
    padding: 5px 9px;
    border-radius: 6px;
    background: #e8f7ed;
    color: #145c39;
    font-size: 12px;
    font-weight: 800;
  }

  .wg-highlight-title {
    display: block;
    margin: 0 0 8px;
    font-family: var(--wg-font-heading);
    color: #10251a;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.28;
  }

  p {
    margin: 0;
    color: #405147;
    font-size: 15px;
    line-height: 1.7;
  }
}

.wg-toc,
.wg-info-card,
.wg-download-box {
  margin: 28px 0;
  padding: 24px;
  border-radius: 8px;
  background: #f4fbf6;
  border: 1px solid #dbeee2;
}

.wg-page-download .wg-info-card {
  background: #ffffff;
  border: 2px solid #bdebd1;
  box-shadow: 0 18px 36px rgba(17, 167, 101, 0.12);
}

.wg-page-compare .wg-info-card {
  background: #ffffff;
  border-color: #c7d2fe;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
}

.wg-toc-title,
.wg-info-card h2,
.wg-section h2,
.wg-faq h2,
.wg-sticky-title {
  display: block;
  margin: 0 0 16px;
  font-family: var(--wg-font-heading);
  color: #10251a;
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: 0;
  font-weight: 400;
}

.wg-toc ol {
  columns: 2;
  margin: 0;
  padding-left: 20px;
  color: #334139;
  line-height: 1.8;
}

.wg-toc li {
  break-inside: avoid;
}

.wg-toc a {
  color: #183329;
  text-decoration: none;
}

.wg-toc a:hover {
  color: var(--wg-accent-dark);
  text-decoration: underline;
  text-underline-offset: 4px;
}

.wg-info-card table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border-radius: 8px;
}

.wg-page-compare .wg-info-card table {
  min-width: 640px;
}

.wg-info-card th,
.wg-info-card td {
  padding: 14px 16px;
  border-bottom: 1px solid #dbeee2;
  text-align: left;
}

.wg-info-card th {
  width: 34%;
  color: #145c39;
  background: #e8f7ed;
}

.wg-home-carousel {
  position: relative;
  margin: 30px 0 8px;
  padding: 18px 52px 42px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #dbeee2;
  box-shadow: 0 14px 34px rgba(15, 31, 25, 0.08);
  overflow: hidden;
}

.wg-carousel-track {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - 32px) / 3);
  gap: 16px;
  max-width: 100%;
  overflow: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
}

.wg-carousel-track::-webkit-scrollbar {
  display: none;
}

.wg-carousel-slide {
  margin: 0;
  min-width: 0;
  padding: 10px;
  border-radius: 8px;
  background: #f8fbf9;
  border: 1px solid #dbeee2;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.wg-carousel-slide.is-active {
  border-color: #16a34a;
  box-shadow: 0 10px 24px rgba(17, 167, 101, 0.12);
}

.wg-carousel-slide img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 6px;
  border: 1px solid #e0ebe5;
  background: #f6f8f7;
}

.wg-carousel-btn {
  position: absolute;
  top: 50%;
  z-index: 2;
  width: 36px;
  height: 48px;
  border: 1px solid #cfe7d8;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.94);
  color: #145c39;
  cursor: pointer;
  font-size: 32px;
  line-height: 1;
  transform: translateY(-50%);
}

.wg-carousel-btn:hover {
  background: #e8f7ed;
  border-color: #16a34a;
}

.wg-carousel-prev {
  left: 12px;
}

.wg-carousel-next {
  right: 12px;
}

.wg-carousel-dots {
  position: absolute;
  right: 0;
  bottom: 16px;
  left: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.wg-carousel-dots button {
  width: 9px;
  height: 9px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #cfe7d8;
  cursor: pointer;
}

.wg-carousel-dots button.is-active {
  width: 24px;
  background: #16a34a;
}

.wg-table-head th,
.wg-table-head td {
  background: #145c39;
  color: #fff;
  font-weight: 700;
}

.wg-section {
  margin-top: 34px;
  scroll-margin-top: 24px;
}

.wg-section-title {
  margin: 0 0 16px;
  font-family: var(--wg-font-heading);
  color: #10251a;
  font-size: 24px !important;
  font-weight: 400;
  line-height: 1.2 !important;
}

.wg-faq {
  scroll-margin-top: 24px;
}

.wg-conclusion {
  margin-top: 38px;
  padding: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f4fbf6 0%, #ffffff 100%);
  border: 1px solid #dbeee2;
  scroll-margin-top: 24px;
}

.wg-conclusion h2 {
  margin: 0 0 14px;
  font-family: var(--wg-font-heading);
  color: #10251a;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.2;
}

.wg-conclusion p {
  margin: 0;
}

.wg-subsections {
  display: grid;
  gap: 16px;
  margin-top: 18px;
}

.wg-subsection {
  padding: 16px;
  border-radius: 8px;
  background: #f7faf8;
  border: 1px solid #dbeee2;

  h3 {
    margin: 0 0 8px;
    font-family: var(--wg-font-heading);
    color: #10251a;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.2;
  }
}


.wg-section-image {
  margin: 8px 0 22px;
  padding: 18px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border: 1px solid #e5ece8;

  img {
    max-height: 280px;
    object-fit: contain;
  }
}

.wg-section-home.wg-section-with-image .wg-section-image {
  display: flex;
  justify-content: center;
  background: linear-gradient(135deg, #071b13, #0f5136);
  border-color: rgba(20, 92, 57, 0.28);

  img {
    width: auto;
    max-width: 100%;
    max-height: 360px;
    object-fit: contain;
    box-shadow: 0 18px 38px rgba(5, 20, 14, 0.28);
  }
}

.wg-section-update,
.wg-section-install {
  position: relative;
  margin: 0 0 16px;
  padding: 28px 28px 28px 82px;
  background: #ffffff;
  border: 1px solid #dbe7f5;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(18, 32, 26, 0.05);
}

.wg-section-install {
  margin-bottom: 20px;
  padding: 30px 30px 30px 92px;
  background: linear-gradient(135deg, #ffffff 0%, #fbfffd 100%);
  border-color: #cfe9df;
  box-shadow: 0 16px 38px rgba(15, 63, 60, 0.08);
}

.wg-section-update {
  margin-bottom: 18px;
  padding: 28px 28px 28px 90px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border-color: #d7e3f7;
  border-left: 5px solid #2563eb;
  box-shadow: 0 14px 34px rgba(30, 58, 138, 0.07);
}

.wg-section-update h2 {
  color: #1e3a8a;
}

.wg-section-update .wg-list {
  columns: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  margin-top: 18px;
}

.wg-section-update .wg-list li {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #eef5ff;
  border: 1px solid #d7e3f7;
}

.wg-section-install.wg-section-with-image {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(210px, 300px);
  gap: 24px;
  align-items: start;
}

.wg-section-install.wg-section-with-image > :not(.wg-section-image):not(.wg-step-number) {
  grid-column: 1;
}

.wg-section-install .wg-section-image {
  grid-column: 2;
  grid-row: 1 / span 8;
  align-self: start;
  display: flex;
  justify-content: center;
  padding: 12px;
  background: linear-gradient(135deg, #f8fffb, #eefaf5);
  border-color: rgba(15, 63, 60, 0.2);

  img {
    width: 100%;
    max-width: 100%;
    max-height: 360px;
    object-fit: contain;
    box-shadow: 0 16px 34px rgba(15, 63, 60, 0.12);
  }
}

.wg-section-install h2 {
  color: #0f3f3c;
}

.wg-section-install .wg-list {
  columns: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  margin-top: 18px;
}

.wg-section-install .wg-list li {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #eefaf5;
}

.wg-section-update::before,
.wg-section-install::before {
  content: '';
  position: absolute;
  left: 48px;
  top: 70px;
  bottom: -18px;
  width: 2px;
  background: #d7e3f7;
}

.wg-section-update:last-of-type::before,
.wg-section-install:last-of-type::before {
  display: none;
}

.wg-step-number {
  position: absolute;
  left: 24px;
  top: 24px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--wg-accent);
  font-weight: 800;
}

.wg-section-install .wg-step-number {
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  box-shadow: 0 10px 22px rgba(13, 148, 136, 0.22);
}

.wg-section-update .wg-step-number {
  background: linear-gradient(135deg, #2563eb, #38bdf8);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.22);
}

.wg-section-notWorking,
.wg-section-banned,
.wg-section-safe {
  padding: 24px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eadfd0;
  border-left: 6px solid var(--wg-accent);
}

.wg-section-banned {
  background: #fffafa;
  border-color: #fecaca;
}

.wg-section-safe {
  padding: 26px;
  background: linear-gradient(135deg, #ffffff 0%, #fffaf3 100%);
  border-color: #fed7aa;
  border-left-color: #d97706;
  box-shadow: 0 14px 32px rgba(124, 45, 18, 0.06);
}

.wg-section-safe h2 {
  color: #7c2d12;
}

.wg-section-safe .wg-section-image {
  background: #111827;
  border-color: #1f2937;

  img {
    max-height: 320px;
    box-shadow: 0 16px 34px rgba(17, 24, 39, 0.22);
  }
}

.wg-section-safe .wg-list {
  columns: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  margin-top: 18px;
}

.wg-section-safe .wg-list li {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.wg-risk-label {
  display: inline-flex;
  margin-bottom: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--wg-soft);
  color: var(--wg-accent-dark);
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.wg-section-safe .wg-risk-label {
  background: #1f2937;
  color: #ffffff;
}

.wg-section-compare {
  padding: 24px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #d8ddff;
}

.wg-section-compare.wg-section-table {
  padding: 24px 0;
  margin-top: 28px;
  border: 0;
  border-top: 1px solid #d8ddff;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.wg-section-compare.wg-section-table:first-of-type {
  margin-top: 24px;
  border-top: 0;
}

.wg-page-compare .wg-table-head th,
.wg-page-compare .wg-table-head td {
  background: var(--wg-accent-dark);
}

.wg-type-legal .wg-section {
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.wg-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 0;
  margin: 18px 0;
  list-style: none;

  li {
    padding: 13px 14px;
    border-radius: 6px;
    background: #f7faf8;
    border: 1px solid #e1ebe5;
    color: #26352d;
  }
}

.wg-inline-link {
  margin-top: 8px;
  color: #145c39;
  background: #e8f7ed;
}

.wg-download-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 6px;
    color: #607067;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 0;
    min-height: 46px;
    padding: 0 22px;
    border-radius: 999px;
    background: var(--wg-accent);
    color: #fff;
    font-family: var(--wg-font-heading);
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
  }
}

.wg-download-note {
  padding: 12px 14px;
  border-radius: 6px;
  background: #fff7e6;
  color: #7a4b00 !important;
}

.wg-faq {
  margin-top: 42px;
}

.wg-faq-item {
  padding: 18px 0;
  border-bottom: 1px solid #e4ece7;

  h3 {
    margin: 0 0 8px;
    font-family: var(--wg-font-heading);
    color: #10251a;
    font-size: 24px;
    font-weight: 400;
  }
}

.wg-faq-question {
  display: block;
  margin: 0 0 8px;
  font-family: var(--wg-font-heading);
  color: #10251a;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.2;
}

.wg-disclaimer {
  margin-top: 28px;
  padding: 18px;
  border-radius: 8px;
  background: #fff7e6;
  border: 1px solid #f4d39b;
  color: #6b4a12;
  line-height: 1.7;
  font-weight: 600;
}

.wg-sticky-card {
  position: sticky;
  top: 18px;
  padding: 22px;

  dl {
    margin: 16px 0;
  }

  div {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #e4ece7;
  }

  dt {
    color: #647067;
  }

  dd {
    margin: 0;
    font-weight: 700;
    color: #10251a;
  }

  .wg-primary-btn {
    width: 100%;
    box-sizing: border-box;
  }

  p {
    color: #647067;
    line-height: 1.6;
    font-size: 14px;
  }
}

.wg-page a {
  color: #167a46;
}

.wg-page .wg-primary-btn {
  color: #ffffff;
}

.wg-page .wg-secondary-btn,
.wg-page .wg-inline-link {
  color: var(--wg-accent-dark);
}

.wg-hero-home .wg-secondary-btn {
  color: #ffffff;
}

@media (max-width: 900px) {
  .wg-hero,
  .wg-content {
    grid-template-columns: 1fr;
  }

  .wg-hero {
    padding: 28px;
  }

  .wg-hero-download .wg-download-actions .wg-primary-btn {
    min-width: 0;
    width: 100%;
  }

  .wg-hero-home {
    min-height: auto;
    gap: 20px;
    padding: 28px 24px;
  }

  .wg-hero-update {
    grid-template-columns: 1fr;
  }

  .wg-update-hero-checks {
    grid-template-columns: 1fr;
  }

  .wg-hero-update .wg-hero-media {
    justify-self: stretch;
    width: 100%;
  }

  .wg-hero-safe {
    grid-template-columns: 1fr;
  }

  .wg-safe-hero-audit {
    grid-template-columns: 1fr;
  }

  .wg-hero-safe .wg-hero-media {
    justify-self: stretch;
    width: 100%;
  }

  .wg-hero-install {
    grid-template-columns: 1fr;
  }

  .wg-install-hero-steps {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .wg-hero-install .wg-hero-media {
    width: 100%;
  }

  .wg-section-install.wg-section-with-image {
    grid-template-columns: 1fr;
  }

  .wg-section-install .wg-section-image {
    grid-column: 1;
    grid-row: auto;
    min-height: auto;
  }

  .wg-sidebar {
    display: none;
  }

  .wg-layout-guide .wg-article > .wg-toc {
    display: block;
  }

  .wg-faq-hub {
    grid-template-columns: 1fr;
  }

  .wg-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .wg-carousel-track {
    grid-auto-columns: calc((100% - 14px) / 2);
    gap: 14px;
  }
}

@media (max-width: 640px) {
  .wg-main {
    width: min(100% - 20px, 1180px);
    padding-top: 12px;
  }

  .wg-article {
    padding: 22px 16px;
  }

  .wg-toc ol,
  .wg-list,
  .wg-summary-grid,
  .wg-highlight-grid {
    columns: 1;
    grid-template-columns: 1fr;
  }

  .wg-home-panel {
    padding: 20px 16px;
  }

  .wg-home-panel-head {
    display: block;
  }

  .wg-home-carousel {
    margin-top: 24px;
    padding: 12px 12px 42px;
  }

  .wg-carousel-track {
    grid-auto-columns: 100%;
    gap: 12px;
  }

  .wg-carousel-slide {
    padding: 8px;
  }

  .wg-carousel-btn {
    top: auto;
    bottom: 9px;
    width: 34px;
    height: 30px;
    font-size: 24px;
    transform: none;
  }

  .wg-carousel-prev {
    left: 12px;
  }

  .wg-carousel-next {
    right: 12px;
  }

  .wg-carousel-dots {
    bottom: 19px;
  }

  .wg-download-box {
    align-items: stretch;
    flex-direction: column;
  }

  .wg-hero h1 {
    font-size: 34px;
  }

  .wg-hero-home h1 {
    font-size: 34px;
  }

  .wg-hero-home .wg-subtitle {
    font-size: 17px;
    line-height: 1.48;
  }

  .wg-hero-download {
    padding: 24px 18px;
  }

  .wg-hero-update {
    padding: 26px 18px;
  }

  .wg-hero-safe {
    padding: 26px 18px;
  }

  .wg-hero-notWorking.wg-hero-no-media,
  .wg-hero-banned.wg-hero-no-media,
  .wg-hero-compare.wg-hero-no-media {
    padding: 24px 16px;
  }

  .wg-hero-notWorking.wg-hero-no-media h1,
  .wg-hero-banned.wg-hero-no-media h1,
  .wg-hero-compare.wg-hero-no-media h1 {
    font-size: 30px;
    line-height: 1.18;
  }

  .wg-hero-notWorking.wg-hero-no-media .wg-subtitle,
  .wg-hero-banned.wg-hero-no-media .wg-subtitle,
  .wg-hero-compare.wg-hero-no-media .wg-subtitle,
  .wg-page-notWorking .wg-intro p,
  .wg-page-banned .wg-intro p,
  .wg-page-compare .wg-intro p,
  .wg-page-notWorking .wg-section p,
  .wg-page-banned .wg-section p,
  .wg-page-compare .wg-section p {
    font-size: 17px;
    line-height: 1.58;
  }

  .wg-page-compare .wg-info-card {
    padding: 18px 14px;
  }

  .wg-update-hero-checks div {
    min-height: auto;
  }

  .wg-safe-hero-audit div {
    min-height: auto;
  }

  .wg-hero-install {
    padding: 24px 16px;
  }

  .wg-hero-install.wg-hero-no-media h1 {
    font-size: 30px;
    line-height: 1.18;
  }

  .wg-hero-install.wg-hero-no-media .wg-subtitle {
    font-size: 17px;
    line-height: 1.58;
  }

  .wg-install-hero-steps span {
    min-height: 50px;
  }

  .wg-section-update,
  .wg-section-install {
    padding: 82px 18px 22px;
  }

  .wg-section-update::before,
  .wg-section-install::before {
    display: none;
  }

  .wg-section-update .wg-list,
  .wg-section-install .wg-list,
  .wg-section-safe .wg-list {
    grid-template-columns: 1fr;
  }

  .wg-apk-summary {
    grid-template-columns: 1fr;
  }
}
</style>
